import React from "react";

interface PaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void; // eslint-disable-line no-unused-vars
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  pageCount,
  onPageChange,
}) => {
  // 表示するページ番号の配列を生成（常に5つ表示）
  const showPages: number[] = [];
  const maxDisplay = 5;

  if (pageCount <= maxDisplay) {
    // 総ページ数が5以下なら全て表示
    for (let i = 1; i <= pageCount; i += 1) {
      showPages.push(i);
    }
  } else {
    // 5つ表示：現在のページを中心に配置（端では調整）
    let startPage = Math.max(1, page - 2);
    const endPage = Math.min(pageCount, startPage + maxDisplay - 1);

    // 右端に寄りすぎた場合、左にずらす
    if (endPage - startPage < maxDisplay - 1) {
      startPage = Math.max(1, endPage - maxDisplay + 1);
    }

    for (let i = startPage; i <= endPage; i += 1) {
      showPages.push(i);
    }
  }

  const setFirstPage = () => {
    if (page === 1) return;
    onPageChange(1);
  };

  const setPreviousPage = () => {
    if (page === 1) return;
    onPageChange(page - 1);
  };

  const setPage = (toPage: number) => {
    if (page === toPage) return;
    onPageChange(toPage);
  };

  const setNextPage = () => {
    if (page === pageCount) return;
    onPageChange(page + 1);
  };

  const setLastPage = () => {
    if (page === pageCount) return;
    onPageChange(pageCount);
  };

  return (
    <nav className="flex justify-center py-2">
      <ol className="inline-flex -space-x-px text-base h-10">
        <li>
          <button
            type="button"
            onClick={setFirstPage}
            className="flex items-center justify-center px-4 h-10 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700"
          >
            <span className="hidden md:inline-flex">最初のページ</span>
            <span className="md:hidden inline-flex">&laquo;</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={setPreviousPage}
            className="flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
          >
            <span className="hidden md:inline-flex">前へ</span>
            <span className="md:hidden inline-flex">&lt;</span>
          </button>
        </li>
        {showPages.map((showPage) => (
          <li key={showPage}>
            <button
              type="button"
              onClick={() => setPage(showPage)}
              className={
                page === showPage
                  ? "flex items-center justify-center px-4 h-10 text-blue-600 border border-gray-300 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                  : "flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
              }
            >
              {showPage}
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={setNextPage}
            className="flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
          >
            <span className="hidden md:inline-flex">次へ</span>
            <span className="md:hidden inline-flex">&gt;</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={setLastPage}
            className="flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700"
          >
            <span className="hidden md:inline-flex">最後のページ</span>
            <span className="md:hidden inline-flex">&raquo;</span>
          </button>
        </li>
      </ol>
    </nav>
  );
};

export default Pagination;
